import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useToast } from '@/hooks/use-toast';
import { Shield, ShieldCheck, ShieldOff, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const MFAEnroll = () => {
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [enrolledFactors, setEnrolledFactors] = useState<{ id: string; friendly_name?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [unenrolling, setUnenrolling] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadFactors();
  }, []);

  const loadFactors = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      const verified = (data?.totp || []).filter(f => f.status === 'verified');
      setEnrolledFactors(verified.map(f => ({ id: f.id, friendly_name: f.friendly_name || undefined })));
    } catch (error: any) {
      console.error('Error loading MFA factors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'CompresorJPEG Admin'
      });
      if (error) throw error;
      setFactorId(data.id);
      setQrUri(data.totp.uri);
    } catch (error: any) {
      toast({
        title: 'Error al configurar 2FA',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setEnrolling(false);
    }
  };

  const handleVerify = async () => {
    if (!factorId || verifyCode.length !== 6) return;
    setVerifying(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode
      });
      if (verifyError) throw verifyError;

      toast({
        title: '2FA activado',
        description: 'La autenticación de dos factores se ha configurado correctamente.'
      });
      setQrUri(null);
      setFactorId(null);
      setVerifyCode('');
      await loadFactors();
    } catch (error: any) {
      toast({
        title: 'Código inválido',
        description: 'El código ingresado no es correcto. Intenta de nuevo.',
        variant: 'destructive'
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleUnenroll = async (id: string) => {
    setUnenrolling(true);
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: id });
      if (error) throw error;
      toast({
        title: '2FA desactivado',
        description: 'La autenticación de dos factores se ha eliminado.'
      });
      await loadFactors();
    } catch (error: any) {
      toast({
        title: 'Error al desactivar 2FA',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setUnenrolling(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Autenticación de Dos Factores (2FA)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Cargando...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {enrolledFactors.length > 0 ? (
            <ShieldCheck className="h-5 w-5 text-green-600" />
          ) : (
            <Shield className="h-5 w-5 text-primary" />
          )}
          Autenticación de Dos Factores (2FA)
        </CardTitle>
        <CardDescription>
          {enrolledFactors.length > 0
            ? '2FA está activo. Tu cuenta está protegida con autenticación TOTP.'
            : 'Agrega una capa extra de seguridad a tu cuenta con una app de autenticación.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {enrolledFactors.length > 0 && !qrUri && (
          <div className="space-y-3">
            {enrolledFactors.map(factor => (
              <div key={factor.id} className="flex items-center justify-between p-3 border rounded-md bg-muted/50">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium">TOTP activo</span>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleUnenroll(factor.id)}
                  disabled={unenrolling}
                >
                  {unenrolling ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4 mr-1" />}
                  Desactivar
                </Button>
              </div>
            ))}
          </div>
        )}

        {enrolledFactors.length === 0 && !qrUri && (
          <Button onClick={handleEnroll} disabled={enrolling}>
            {enrolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
            Configurar 2FA
          </Button>
        )}

        {qrUri && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Escanea este código QR con tu app de autenticación (Google Authenticator, Authy, etc.)
            </p>
            <div className="flex justify-center p-4 bg-white rounded-lg border">
              <QRCodeSVG value={qrUri} size={200} />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Ingresa el código de 6 dígitos para verificar:</p>
              <div className="flex items-center gap-3">
                <InputOTP maxLength={6} value={verifyCode} onChange={setVerifyCode}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
                <Button onClick={handleVerify} disabled={verifying || verifyCode.length !== 6}>
                  {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verificar'}
                </Button>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { setQrUri(null); setFactorId(null); setVerifyCode(''); }}>
              Cancelar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MFAEnroll;
